import type { OrganizationMember, Tables } from "@msuite/utils/types";
import type { Editor, Range } from "@tiptap/react";
import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { BlankSlateInline } from "../../blank-slate";
import { icon } from "../../icons";
import { useTranslation } from "../../localization";

export type MentionMemberType = "member";
export type MentionProjectType = "project";
export type MentionType = MentionMemberType | MentionProjectType;

export interface MentionUserItem {
	type: MentionMemberType;
	item: OrganizationMember;
}

export interface MentionProjectItem {
	type: MentionProjectType;
	item: Tables<"projects">;
}

export type MentionItem = MentionUserItem | MentionProjectItem;

export interface MentionListProps {
	items: MentionItem[];
	command: (item: { id: string }) => void;
	mentionRenderers: MentionRenderers;
	editor: Editor;
	range: Range;
}

type MentionTypeToItem = {
	member: Tables<"users">;
	project: Tables<"projects">;
};

export type MentionRenderers = {
	[K in keyof MentionTypeToItem]: (id: string, type: "list" | "inline") => React.ReactNode;
};

export const MentionList = forwardRef((props: MentionListProps, ref) => {
	/** State */
	const [selectedIndex, setSelectedIndex] = useState(0);

	/** Props */
	const renderers = props.mentionRenderers;
	const editor = props.editor;

	/** Hooks */
	const { t } = useTranslation();

	/** Functions */
	function selectItem(index: number) {
		const item = props.items[index];

		if (item) {
			if (item.type === "member") {
				handleOnSelectItem("member", item.item.user.id, item.item.user.name ?? "");
			} else if (item.type === "project") {
				handleOnSelectItem("project", item.item.id, item.item.name);
			}
		}
	}

	function handleOnSelectItem(type: MentionType, id: string, label: string) {
		editor
			.chain()
			.focus()
			.deleteRange(props.range)
			.insertContent({
				attrs: {
					id,
					label,
					type,
				},
				type: "mention",
			})
			.run();
	}

	function upHandler() {
		setSelectedIndex((selectedIndex + props.items.length - 1) % props.items.length);
	}

	function downHandler() {
		setSelectedIndex((selectedIndex + 1) % props.items.length);
	}

	function enterHandler() {
		selectItem(selectedIndex);
	}

	/** Effects */
	useEffect(() => {
		return setSelectedIndex(0);
	}, [props.items]);

	useImperativeHandle(ref, () => ({
		onKeyDown: ({ event }: any) => {
			if (event.key === "ArrowUp") {
				upHandler();
				return true;
			}

			if (event.key === "ArrowDown") {
				downHandler();
				return true;
			}

			if (event.key === "Enter") {
				enterHandler();
				return true;
			}

			return false;
		},
	}));

	return (
		<div className="dropdown-menu">
			{props.items.length ? (
				props.items.map((item: MentionItem, index: number) => (
					<React.Fragment key={index}>
						<button
							className={index === selectedIndex ? "is-selected" : ""}
							onClick={() => selectItem(index)}
							type="button"
						>
							{renderers[item.type](
								item.type === "member" ? item.item.user.id : item.item.id,
								"list",
							)}
						</button>
					</React.Fragment>
				))
			) : (
				<div className="listbox-option">
					<BlankSlateInline
						Icon={icon("Search")}
						label={t("mentions.type_to_find_member_or_project")}
					/>
				</div>
			)}
		</div>
	);
});
